import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { msalInstance, loginRequest } from '@/lib/msal-config';
import { AuthenticationResult } from '@azure/msal-browser';
import { env } from '@/lib/env';
import { get } from '@/lib/api-client';
import { websocketClient } from '@/services/websocket-client';
import type { User } from '@/types';

export interface EntraAuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  // Impersonation support
  isImpersonating: boolean;
  impersonatedUser: User | null;
  realUser: User | null;
  startImpersonation: (targetUser: User, expiresAt: string) => void;
  stopImpersonation: () => void;
}

const EntraAuthContext = createContext<EntraAuthContextType | undefined>(undefined);

const API_URL = env.API_URL;
const IMPERSONATION_STATE_KEY = 'impersonation_state';

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUser: User;
  realUser: User;
  expiresAt: string;
}

export function EntraAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [realUser, setRealUser] = useState<User | null>(null);

  useEffect(() => {
    initializeAuth();

    // Restore impersonation state from localStorage and verify with backend
    const restoreImpersonation = async () => {
      const stored = localStorage.getItem(IMPERSONATION_STATE_KEY);
      if (!stored) return;

      try {
        const state: ImpersonationState = JSON.parse(stored);
        const response = await get<{ success: boolean; data: { isActive: boolean } }>('/admin/impersonate/status');
        if (response.data.isActive) {
          setIsImpersonating(true);
          setImpersonatedUser(state.impersonatedUser);
          setRealUser(state.realUser);
          setUser(state.impersonatedUser);
        } else {
          localStorage.removeItem(IMPERSONATION_STATE_KEY);
        }
      } catch {
        localStorage.removeItem(IMPERSONATION_STATE_KEY);
      }
    };

    restoreImpersonation();
  }, []);

  async function initializeAuth() {
    try {
      if (env.isDev) console.log('🔄 initializeAuth: Starting...');
      await msalInstance.initialize();

      // Handle redirect response
      const response = await msalInstance.handleRedirectPromise();

      if (response) {
        if (env.isDev) console.log('✅ initializeAuth: Got redirect response, handling auth...');
        await handleAuthResponse(response);
      } else {
        // Check if user is already logged in
        const accounts = msalInstance.getAllAccounts();
        if (env.isDev) console.log('🔍 initializeAuth: Checking existing accounts, found:', accounts.length);
        
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0] || null);
          
          // Check if we already have a valid token in localStorage
          const existingToken = localStorage.getItem('auth_token');
          if (existingToken) {
            if (env.isDev) console.log('✅ initializeAuth: Found existing token in localStorage');
            // Try to load user with existing token
            await loadUser();
          } else {
            if (env.isDev) console.log('⚠️ initializeAuth: No token in localStorage, will acquire new one');
            await loadUser();
          }
        } else {
          if (env.isDev) console.log('⚠️ initializeAuth: No accounts found');
        }
      }
    } catch (error) {
      console.error('❌ Auth initialization error:', error);
    } finally {
      if (env.isDev) console.log('✅ initializeAuth: Complete, setting isLoading = false');
      setIsLoading(false);
    }
  }

  async function handleAuthResponse(response: AuthenticationResult) {
    try {
      if (env.isDev) console.log('🔄 handleAuthResponse: Processing auth response...');
      msalInstance.setActiveAccount(response.account);

      // Use the ID token from MSAL response
      const idToken = response.idToken;

      if (!idToken) {
        throw new Error('No ID token received from Microsoft');
      }

      if (env.isDev) console.log('📝 handleAuthResponse: Got ID token, storing immediately...');
      
      // Store tokens IMMEDIATELY, before any async operations
      localStorage.setItem('auth_token', idToken);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', idToken);
      localStorage.setItem('tokenExpiry', String(Date.now() + (response.expiresOn?.getTime() || 3600000)));

      // Send ID token to backend for verification and user creation
      if (env.isDev) console.log('🔄 handleAuthResponse: Sending token to backend...');
      const backendResponse = await fetch(`${API_URL}/api/auth/entra/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: idToken,
        }),
      });

      if (!backendResponse.ok) {
        const errorData = await backendResponse.json().catch(() => ({}));
        console.error('❌ Backend authentication error:', errorData);
        
        // If user already exists, that's actually fine - just means they logged in before
        if (errorData.message?.includes('duplicate key value violates unique constraint')) {
          // Try to verify the token to get user info
          const verifyResponse = await fetch(`${API_URL}/api/auth/entra/verify`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            },
          });
          
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            setUser(verifyData.user);
            // Token is already stored in localStorage above — connect WebSocket
            // for this returning user the same way the happy path does.
            websocketClient.connect(idToken);
            return;
          }
        }
        
        throw new Error(errorData.error || 'Failed to authenticate with backend');
      }

      const data = await backendResponse.json();
      
      // Set user state (this will trigger navigation)
      setUser(data.user);

      // Connect WebSocket with the stored ID token so real-time
      // notifications and status updates work in Entra mode.
      websocketClient.connect(idToken);
    } catch (error) {
      console.error('❌ Auth response handling error:', error);
      throw error;
    }
  }

  async function loadUser() {
    try {
      const token = await getAccessToken();
      if (!token) {
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/entra/verify`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store the token in localStorage for API client
        localStorage.setItem('auth_token', token);
        
        setUser(data.user);

        // Connect WebSocket now that the session is confirmed valid.
        websocketClient.connect(token);
      } else if (response.status === 401 || response.status === 403) {
        console.error('❌ loadUser: Token verification failed, status:', response.status);
        // Only logout on actual authentication failures (401/403)
        await logout();
      } else {
        console.error('❌ loadUser: Token verification failed, status:', response.status);
        // Don't logout on other errors (network issues, server errors, etc.)
      }
    } catch (error) {
      console.error('❌ loadUser error:', error);
      // Don't logout on network errors, only on auth failures
    }
  }

  async function login() {
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async function logout() {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem(IMPERSONATION_STATE_KEY);
      setUser(null);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setRealUser(null);

      // Disconnect WebSocket on sign-out
      websocketClient.disconnect();

      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.origin}/login`,
        account: msalInstance.getActiveAccount(),
      });
    } catch (error) {
      console.error('❌ Logout error:', error);
      window.location.href = '/login';
    }
  }

  async function getAccessToken(): Promise<string | null> {
    try {
      const account = msalInstance.getActiveAccount();
      if (!account) return null;

      // Try to get token silently
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      return response.idToken;
    } catch (error) {
      console.error('Token acquisition error:', error);
      
      // If silent acquisition fails, try interactive
      try {
        await msalInstance.acquireTokenRedirect(loginRequest);
        return null;
      } catch (redirectError) {
        console.error('Token redirect error:', redirectError);
        return null;
      }
    }
  }

  const startImpersonation = useCallback((targetUser: User, expiresAt: string) => {
    if (!user) return;

    const state: ImpersonationState = {
      isImpersonating: true,
      impersonatedUser: targetUser,
      realUser: user,
      expiresAt,
    };
    localStorage.setItem(IMPERSONATION_STATE_KEY, JSON.stringify(state));

    setRealUser(user);
    setImpersonatedUser(targetUser);
    setIsImpersonating(true);
    setUser(targetUser);
  }, [user]);

  const stopImpersonation = useCallback(() => {
    localStorage.removeItem(IMPERSONATION_STATE_KEY);

    if (realUser) {
      setUser(realUser);
    }
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setRealUser(null);
  }, [realUser]);

  return (
    <EntraAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        getAccessToken,
        isImpersonating,
        impersonatedUser,
        realUser,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </EntraAuthContext.Provider>
  );
}

export function useEntraAuth() {
  const context = useContext(EntraAuthContext);
  if (!context) {
    throw new Error('useEntraAuth must be used within EntraAuthProvider');
  }
  return context;
}
